/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import "./index.css";
import { TimerFont } from "./styled";
import { useReactMediaRecorder } from "react-media-recorder";
import { useCountdownTimer } from "../AudioRecorder/timer";
import { RecordingSpinner2 } from "./RecorderFrame";
import { chatService } from "../../../../services";
import { RotatingLines } from "react-loader-spinner";
import { app } from "@microsoft/teams-js";
import { useAppDispatch } from "../../../../redux/hooks";
import { actionDisplayNotification } from "../../../../redux/notificationSlice";
import { useTranslation } from "react-i18next";

interface IAudioRecorderProps {
  onCancel: VoidFunction;
  setSpeechResponse: (value: string) => void;
}

export interface AudioRecorderHandles {
  triggerAction: () => void;
}

const AudioRecorder2 = forwardRef<AudioRecorderHandles, IAudioRecorderProps>(
  ({ onCancel, setSpeechResponse }, ref) => {
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [shouldRequest, setShouldRequest] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isInTeams, setIsInTeams] = useState(false);

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Inicializar a SDK do Teams
      app
        .initialize()
        .then(() => {
          // Verificar se a aplicação está sendo executada dentro do Teams
          app
            .getContext()
            .then(async (context: app.Context) => {
              if (context) {
                setIsInTeams(true);
              }
            })
            .catch((error) => {
              console.error("Erro ao obter o contexto do Teams:", error);
            });
        })
        .catch((error) => {
          console.error("Erro ao inicializar o Microsoft Teams SDK:", error);
        });
    }, []);

    const { startRecording, stopRecording, clearBlobUrl, error } = useReactMediaRecorder({
      audio: true,
      stopStreamsOnStop: true,
      onStart() {
        startTimer();
        setIsRecording(true);
      },
      onStop(blobUrl: string, blob: Blob) {
        setAudioBlob(blob);
        setIsRecording(false);
      },
    });

    const stopRecordingAndRequest = () => {
      setShouldRequest(true);
      stopRecording();
    };

    const { timeLeft, startTimer } = useCountdownTimer({
      seconds: 30,
      onCountdownExpired: stopRecordingAndRequest,
    });

    useEffect(() => {
      if (isInTeams) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(function (stream) {
            console.log("Microphone access granted");
            // Use the stream
          })
          .catch(function (err) {
            dispatch(
              actionDisplayNotification({
                messages: [t("pages.chat.components.notifications.recordingIsBlocked")],
              }),
            );
          });
      } else {
        clearBlobUrl();
        startRecording();
      }
      return () => {
        stopRecording();
      };
    }, [isInTeams]);

    const handleAudioSend = async (blob: Blob) => {
      const result = await chatService.sendAudioForTranscription({ audio: blob });
      if ("transcription" in result && typeof result.transcription === "string") {
        setSpeechResponse(result.transcription);
      }
      onCancel();
    };

    const handleCancelAudio = () => {
      stopRecording();
      onCancel();
    };

    if (error !== "") {
      handleCancelAudio();
      dispatch(
        actionDisplayNotification({
          messages: [t("pages.chat.components.notifications.recordingIsBlocked")],
        }),
      );
    }

    useEffect(() => {
      if (audioBlob && shouldRequest) {
        handleAudioSend(audioBlob);
      }
    }, [audioBlob]);

    // Expor método para o componente pai
    useImperativeHandle(ref, () => ({
      triggerAction() {
        stopRecordingAndRequest();
      },
    }));

    const formatTimer = (time: number | null) => {
      const formattedNumber = time ? String(time).padStart(2, "0") : "30";
      return `00:${formattedNumber}`;
    };

    return (
      <div className="audio-area">
        <TimerFont>{formatTimer(timeLeft)}</TimerFont>
        {isRecording ? (
          <RecordingSpinner2 />
        ) : (
          <RotatingLines
            width="27"
            strokeColor="#46226A"
            strokeWidth="5"
            animationDuration="0.75"
            ariaLabel="rotating-lines-loading"
          />
        )}
      </div>
    );
  },
);

export default AudioRecorder2;
